<template>
  <div class="auth-page">
    <main class="auth-main">
      <header class="auth-top">
        <BackToSiteButton @click="leave" />
      </header>

      <section class="auth-body">
        <!-- 登录 / 注册 -->
        <template v-if="mode === 'login' || mode === 'signup'">
          <header class="auth-header">
            <h1>{{ mode === 'login' ? $t('auth.welcomeBack') : $t('auth.createAccount') }}</h1>
            <p>{{ mode === 'login' ? $t('auth.loginSubtitle') : $t('auth.signupSubtitle') }}</p>
          </header>

          <MainlandAccessNotice class="auth-mainland-notice" />

          <!-- 邀请码放在两种注册方式之前：Google 注册会直接跳转，必须先填好 -->
          <div v-if="mode === 'signup'" class="auth-referral">
            <button v-if="!showReferral" type="button" class="auth-link auth-referral-toggle" @click="showReferral = true">
              {{ $t('auth.haveReferral') }}
            </button>
            <label v-else class="auth-field">
              <span class="auth-label">{{ $t('referral.signupCodeLabel') }}</span>
              <input
                v-model.trim="referralCode"
                class="auth-input"
                type="text"
                :placeholder="$t('referral.signupCodePlaceholder')"
                autocomplete="off"
                maxlength="6"
                @input="referralCodeError = ''"
                @blur="checkReferralCode"
              >
              <span v-if="referralCodeError" class="auth-field-error">{{ referralCodeError }}</span>
            </label>
          </div>

          <button type="button" class="auth-google" :disabled="loading" @click="continueWithGoogle">
            <svg viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            {{ $t('auth.continueWithGoogle') }}
          </button>

          <div class="auth-divider"><span>{{ $t('auth.orEmail') }}</span></div>

          <form class="auth-form" novalidate @submit.prevent="mode === 'login' ? login() : signup()">
            <label class="auth-field">
              <span class="auth-label">{{ $t('auth.email') }}</span>
              <input
                v-model.trim="email"
                class="auth-input"
                type="email"
                :placeholder="$t('auth.emailPlaceholder')"
                autocomplete="email"
                required
              >
            </label>

            <label class="auth-field">
              <span class="auth-label-row">
                <span class="auth-label">{{ $t('auth.password') }}</span>
                <button v-if="mode === 'login'" type="button" class="auth-link" @click="switchMode('reset')">
                  {{ $t('auth.forgotPassword') }}
                </button>
              </span>
              <span class="auth-input-wrap">
                <input
                  v-model="password"
                  class="auth-input"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="$t('auth.passwordPlaceholder')"
                  :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
                  required
                >
                <button
                  type="button"
                  class="auth-reveal"
                  :aria-label="showPassword ? $t('auth.hidePassword') : $t('auth.showPassword')"
                  @click="showPassword = !showPassword"
                >
                  <span class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
                </button>
              </span>
              <span v-if="mode === 'signup'" class="auth-hint" :class="{ 'is-met': password.length >= 6 }">
                {{ $t('auth.passwordHint') }}
              </span>
            </label>

            <p v-if="error" class="auth-error" role="alert">
              {{ $t(`auth.errors.${error}`) }}
              <button v-if="error === 'emailInUse'" type="button" class="auth-link" @click="switchMode('login')">
                {{ $t('auth.goLogin') }}
              </button>
            </p>

            <button type="submit" class="auth-submit" :disabled="loading">
              <span v-if="loading" class="auth-spinner" aria-hidden="true"></span>
              <template v-if="mode === 'login'">{{ loading ? $t('auth.loggingIn') : $t('auth.loginButton') }}</template>
              <template v-else>{{ loading ? $t('auth.creatingAccount') : $t('auth.createAccountButton') }}</template>
            </button>
          </form>

          <p v-if="mode === 'signup'" class="auth-terms">
            {{ $t('auth.termsPrefix') }}
            <a href="/terms" target="_blank" rel="noopener">{{ $t('auth.termsLink') }}</a>
            {{ $t('auth.termsAnd') }}
            <a href="/privacy" target="_blank" rel="noopener">{{ $t('auth.privacyLink') }}</a>{{ $t('auth.termsSuffix') }}
          </p>

          <p class="auth-switch">
            <template v-if="mode === 'login'">
              {{ $t('auth.noAccount') }}
              <button type="button" class="auth-link" @click="switchMode('signup')">{{ $t('auth.signupButton') }}</button>
            </template>
            <template v-else>
              {{ $t('auth.hasAccount') }}
              <button type="button" class="auth-link" @click="switchMode('login')">{{ $t('auth.loginButton') }}</button>
            </template>
          </p>
        </template>

        <!-- 验证邮箱 -->
        <template v-else-if="mode === 'verify'">
          <div class="auth-state">
            <span class="auth-state-icon material-icons">mark_email_unread</span>
            <h1>{{ $t('auth.verifyTitle') }}</h1>
            <p>{{ $t('auth.verifyText', { email }) }}</p>
            <p class="auth-state-hint">{{ $t('auth.verifyHint') }}</p>

            <p v-if="error" class="auth-error" role="alert">{{ $t(`auth.errors.${error}`) }}</p>
            <p v-else-if="notice" class="auth-notice" role="status">{{ notice }}</p>

            <button type="button" class="auth-submit" @click="switchMode('login')">
              {{ $t('auth.verifiedGoLogin') }}
            </button>
            <button type="button" class="auth-secondary" :disabled="loading || resendCooldown > 0" @click="resendVerification">
              <span v-if="loading" class="auth-spinner" aria-hidden="true"></span>
              {{ resendCooldown > 0 ? $t('auth.resendIn', { seconds: resendCooldown }) : $t('auth.resendVerification') }}
            </button>
          </div>
        </template>

        <!-- 重置密码 -->
        <template v-else-if="mode === 'reset'">
          <header class="auth-header">
            <h1>{{ $t('auth.resetTitle') }}</h1>
            <p>{{ $t('auth.resetSubtitle') }}</p>
          </header>

          <form class="auth-form auth-form-reset" novalidate @submit.prevent="sendReset">
            <label class="auth-field">
              <span class="auth-label">{{ $t('auth.email') }}</span>
              <input
                v-model.trim="email"
                class="auth-input"
                type="email"
                :placeholder="$t('auth.emailPlaceholder')"
                autocomplete="email"
                required
              >
            </label>

            <p v-if="error" class="auth-error" role="alert">{{ $t(`auth.errors.${error}`) }}</p>
            <p v-else-if="notice" class="auth-notice" role="status">{{ notice }}</p>

            <button type="submit" class="auth-submit" :disabled="loading">
              <span v-if="loading" class="auth-spinner" aria-hidden="true"></span>
              {{ loading ? $t('auth.sendingResetLink') : $t('auth.sendResetLink') }}
            </button>
          </form>

          <p class="auth-switch">
            <button type="button" class="auth-link" @click="switchMode('login')">{{ $t('auth.backToLogin') }}</button>
          </p>
        </template>
      </section>
    </main>

    <!-- 左侧只放一句主张和真实播放器截图；截图裁掉顶部标题栏，完整显示不出血 -->
    <aside class="auth-visual" aria-hidden="true">
      <p class="visual-title" :class="{ 'is-zh': $i18n.locale === 'zh' }">{{ $t('auth.visualTitle') }}</p>
      <div class="visual-shot">
        <img :src="$i18n.locale === 'zh' ? playerZh : playerEn" alt="">
      </div>
    </aside>
  </div>
</template>

<script>
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import { auth } from '@/firebase'
import { ensureBackendUser, useAuth } from '@/composables/auth'
import { getAuthReturnPath } from '@/composables/modals'
import { useViews } from '@/composables/views'
import BackToSiteButton from '@/components/common/BackToSiteButton.vue'
import MainlandAccessNotice from '@/components/common/MainlandAccessNotice.vue'
import {
  clearPendingReferralCode,
  getPendingReferralCode,
  setPendingReferralCode,
  validateReferralCode,
} from '@/composables/useReferral'
import playerEn from '@/assets/home/player-en.webp'
import playerZh from '@/assets/home/player-zh.webp'

const RESEND_COOLDOWN_SECONDS = 60

const ROUTE_MODES = { Login: 'login', Signup: 'signup', ResetPassword: 'reset' }
const MODE_ROUTES = { login: 'Login', signup: 'Signup', reset: 'ResetPassword' }

// Firebase 错误码 → 界面文案键；用户自己关掉 Google 弹窗不算错误
const ERROR_KEYS = {
  'auth/invalid-credential': 'invalidCredentials',
  'auth/wrong-password': 'invalidCredentials',
  'auth/user-not-found': 'invalidCredentials',
  'auth/too-many-requests': 'tooManyRequests',
  'auth/network-request-failed': 'networkError',
  'auth/email-already-in-use': 'emailInUse',
  'auth/invalid-email': 'invalidEmail',
  'auth/missing-email': 'invalidEmail',
  'auth/weak-password': 'weakPassword',
  'auth/missing-password': 'weakPassword',
  'auth/popup-blocked': 'popupBlocked',
  'auth/popup-closed-by-user': '',
  'auth/cancelled-popup-request': '',
}

export default {
  name: 'AuthPage',
  components: { BackToSiteButton, MainlandAccessNotice },
  setup() {
    const { user } = useAuth()
    const { enterWorkspaceAfterLogin } = useViews()
    return { user, enterWorkspaceAfterLogin, playerEn, playerZh }
  },
  data() {
    const pendingReferral = getPendingReferralCode()
    return {
      // 验证邮箱是注册或登录过程中的临时状态，没有自己的地址
      verifying: false,
      email: '',
      password: '',
      showPassword: false,
      referralCode: pendingReferral,
      showReferral: Boolean(pendingReferral),
      referralCodeError: '',
      loading: false,
      error: '',
      notice: '',
      resendCooldown: 0,
      cooldownTimer: null,
    }
  },
  computed: {
    mode() {
      return this.verifying ? 'verify' : ROUTE_MODES[this.$route.name]
    },
  },
  watch: {
    // 已登录的人打开登录页，直接送回去；登录注册进行中的临时登录态不算
    user: {
      immediate: true,
      handler(user) {
        if (user && !this.loading) this.leave()
      },
    },
  },
  beforeUnmount() {
    clearInterval(this.cooldownTimer)
  },
  methods: {
    switchMode(mode) {
      this.error = ''
      this.notice = ''
      this.verifying = mode === 'verify'
      if (!this.verifying) this.$router.replace({ name: MODE_ROUTES[mode] })
    },
    leave() {
      return this.$router.replace(getAuthReturnPath())
    },
    // 从购买页过来登录的回到购买页，其余情况照常进工作区
    async finishLogin() {
      await this.leave()
      if (this.$route.name !== 'Pricing') await this.enterWorkspaceAfterLogin(this.$t('audioPlayer.demoVideoName'))
    },
    errorKey(error) {
      const key = ERROR_KEYS[error?.code]
      return key === undefined ? 'unknown' : key
    },
    async login() {
      this.loading = true
      this.error = ''
      try {
        const { user } = await signInWithEmailAndPassword(auth, this.email, this.password)
        if (!user.emailVerified) {
          await signOut(auth)
          this.switchMode('verify')
          return
        }
        await this.finishLogin()
      } catch (error) {
        this.error = this.errorKey(error)
      } finally {
        this.loading = false
      }
    },
    async checkReferralCode() {
      this.referralCodeError = ''
      if (!this.referralCode) return true
      const valid = await validateReferralCode(this.referralCode)
      if (!valid) this.referralCodeError = this.$t('referral.invalidCode')
      return valid
    },
    // 邀请码先暂存在本机，首次初始化用户时由后端绑定
    async prepareReferralCode() {
      if (!this.referralCode) {
        clearPendingReferralCode()
        return true
      }
      if (!(await this.checkReferralCode())) return false
      setPendingReferralCode(this.referralCode)
      return true
    },
    async signup() {
      this.error = ''
      if (this.password.length < 6) {
        this.error = 'weakPassword'
        return
      }
      this.loading = true
      try {
        if (!(await this.prepareReferralCode())) return
        const { user } = await createUserWithEmailAndPassword(auth, this.email, this.password)
        await sendEmailVerification(user)
        await ensureBackendUser(user)
        await signOut(auth)
        this.switchMode('verify')
        this.startResendCooldown()
      } catch (error) {
        this.error = this.errorKey(error)
      } finally {
        this.loading = false
      }
    },
    async continueWithGoogle() {
      this.error = ''
      this.notice = ''
      // Google 弹窗必须紧跟点击，这里不再请求校验邀请码；失焦时已经校验过，后端也会再校验
      if (this.referralCodeError) return
      if (this.referralCode) setPendingReferralCode(this.referralCode)
      else clearPendingReferralCode()

      this.loading = true
      try {
        const { user } = await signInWithPopup(auth, new GoogleAuthProvider())
        await ensureBackendUser(user)
        await this.finishLogin()
      } catch (error) {
        this.error = this.errorKey(error)
      } finally {
        this.loading = false
      }
    },
    // 验证邮件只能由已登录用户发送：临时登录、发送、再退出
    async resendVerification() {
      this.loading = true
      this.error = ''
      this.notice = ''
      try {
        const { user } = await signInWithEmailAndPassword(auth, this.email, this.password)
        await sendEmailVerification(user)
        await signOut(auth)
        this.notice = this.$t('auth.resendSuccess')
        this.startResendCooldown()
      } catch (error) {
        this.error = this.errorKey(error)
      } finally {
        this.loading = false
      }
    },
    startResendCooldown() {
      clearInterval(this.cooldownTimer)
      this.resendCooldown = RESEND_COOLDOWN_SECONDS
      this.cooldownTimer = setInterval(() => {
        this.resendCooldown -= 1
        if (this.resendCooldown <= 0) clearInterval(this.cooldownTimer)
      }, 1000)
    },
    async sendReset() {
      this.loading = true
      this.error = ''
      this.notice = ''
      try {
        await sendPasswordResetEmail(auth, this.email)
        this.notice = this.$t('auth.resetSent', { email: this.email })
      } catch (error) {
        this.error = this.errorKey(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* 整页盖在站点之上：底下的工作区保持原样，登录后回到原处 */
.auth-page {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(440px, 520px);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--surface-panel);
}

/* 右侧：表单 */
.auth-main {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: var(--space-6) var(--space-10) var(--space-10);
}

.auth-body {
  display: flex;
  flex-direction: column;
  width: min(360px, 100%);
  margin: auto;
  padding: var(--space-8) 0;
}

.auth-header h1,
.auth-state h1 {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-2xl);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.auth-header p {
  margin: var(--space-2) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.auth-mainland-notice {
  margin-top: var(--space-4);
}

.auth-google {
  height: 44px;
  margin-top: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 160ms ease, border-color 160ms ease;
}

.auth-google svg {
  width: 18px;
  height: 18px;
}

.auth-google:hover:not(:disabled) {
  border-color: var(--border-active);
  background: var(--surface-subtle);
}

.auth-divider {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: var(--space-5) 0;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-subtle);
}

.auth-form {
  display: grid;
  gap: var(--space-4);
}

.auth-form-reset {
  margin-top: var(--space-6);
}

.auth-field {
  display: grid;
  gap: var(--space-2);
}

.auth-label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.auth-label {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.auth-input-wrap {
  position: relative;
  display: block;
}

.auth-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-md);
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.auth-input-wrap .auth-input {
  padding-right: 44px;
}

.auth-input::placeholder {
  color: var(--ink-tertiary);
}

.auth-input:focus {
  outline: none;
  border-color: var(--signal-active-strong);
  box-shadow: var(--focus-ring-brand);
}

.auth-reveal {
  position: absolute;
  top: 50%;
  right: 6px;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-tertiary);
  transform: translateY(-50%);
  cursor: pointer;
}

.auth-reveal:hover {
  color: var(--ink-primary);
}

.auth-reveal .material-icons {
  font-size: 18px;
}

.auth-hint {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.auth-hint.is-met {
  color: var(--color-success-text);
}

.auth-hint.is-met::before {
  content: '✓ ';
}

.auth-field-error {
  color: var(--color-danger);
  font-size: var(--font-size-xs);
}

.auth-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--signal-active-strong);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

.auth-link:hover {
  text-decoration: underline;
}

.auth-label-row .auth-link {
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.auth-referral {
  margin-top: var(--space-4);
}

.auth-referral + .auth-google {
  margin-top: var(--space-4);
}

.auth-referral-toggle {
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.auth-error,
.auth-notice {
  margin: 0;
  padding: var(--space-3);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.auth-error {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.auth-error .auth-link {
  margin-left: var(--space-1);
}

.auth-notice {
  background: var(--color-success-bg);
  color: var(--color-success-text);
}

.auth-submit,
.auth-secondary {
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: opacity 160ms ease, background-color 160ms ease;
}

.auth-submit {
  border: 1px solid var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.auth-submit:hover:not(:disabled) {
  opacity: 0.88;
}

.auth-secondary {
  border: 1px solid var(--border-default);
  background: var(--surface-panel);
  color: var(--ink-primary);
}

.auth-secondary:hover:not(:disabled) {
  background: var(--surface-subtle);
}

.auth-google:disabled,
.auth-submit:disabled,
.auth-secondary:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.auth-google:focus-visible,
.auth-submit:focus-visible,
.auth-secondary:focus-visible,
.auth-link:focus-visible,
.auth-reveal:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.auth-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: auth-spin 0.8s linear infinite;
}

@keyframes auth-spin {
  to { transform: rotate(360deg); }
}

.auth-terms {
  margin: var(--space-4) 0 0;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.6;
}

.auth-terms a {
  color: var(--ink-secondary);
  text-decoration: underline;
}

.auth-switch {
  margin: var(--space-8) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  text-align: center;
}

/* 验证邮箱 */
.auth-state {
  display: grid;
  gap: var(--space-3);
  text-align: center;
}

.auth-state-icon {
  justify-self: center;
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  margin-bottom: var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--surface-active);
  color: var(--signal-active-strong);
  font-size: 28px;
}

.auth-state p {
  margin: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.auth-state .auth-state-hint {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.auth-state .auth-submit {
  margin-top: var(--space-4);
}

/* 左侧：产品画面。宽度同时受栏宽和视口高度限制，保证截图完整可见 */
.auth-visual {
  --visual-width: min(100%, 1120px, calc((100vh - 280px) * 2512 / 1207));
  position: sticky;
  top: 0;
  order: -1;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-10);
  padding: var(--space-16);
  border-right: 1px solid var(--border-subtle);
  background: var(--color-page);
}

.visual-title {
  width: var(--visual-width);
  margin: 0;
  color: var(--ink-primary);
  font-family: var(--font-family-display);
  font-size: clamp(1.75rem, 2.6vw, 2.5rem);
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.visual-title.is-zh {
  font-family: var(--font-family-display-zh);
  font-weight: 700;
  line-height: 1.3;
}

.visual-shot {
  width: var(--visual-width);
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  box-shadow: var(--shadow-float);
}

/* 原图 2512×1376，顶部 169px 是带返回键的标题栏，只保留下面的部分 */
.visual-shot img {
  display: block;
  width: 100%;
  aspect-ratio: 2512 / 1207;
  object-fit: cover;
  object-position: bottom;
}

@media (prefers-reduced-motion: reduce) {
  .auth-spinner {
    animation: none;
  }
}

@media (max-width: 1024px) {
  .auth-page {
    grid-template-columns: 1fr;
  }

  .auth-visual {
    display: none;
  }
}

@media (max-width: 640px) {
  .auth-main {
    padding: var(--space-4) var(--space-5) var(--space-8);
  }
}
</style>
